import { hex } from "@scure/base";
import type { Address, Hex } from "viem";

import { getReferralHeader, isCooperativeDisabled } from "./config.ts";
import { fetcher } from "./http/fetcher.ts";
import { getLogger } from "./logger.ts";
import { type ContractAddresses, type Contracts, SwapType } from "./types.ts";

export type { ContractAddresses, Contracts };

const cooperativeErrorMessage = "cooperative signatures for swaps are disabled";
const checkCooperative = () => {
    if (isCooperativeDisabled()) {
        throw new Error(cooperativeErrorMessage);
    }
};

// The Boltz API records the referral header for analytics. For `referralId`
// (passed in swap-creation request bodies) we reuse the same value.
const getReferralId = (): string | undefined => getReferralHeader();

type ReverseMinerFees = {
    lockup: number;
    claim: number;
};

type PairLimits = {
    minimal: number;
    maximal: number;
};

type PairType = {
    hash: string;
    rate: number;
};

type SubmarinePairTypeTaproot = PairType & {
    limits: PairLimits & {
        maximalZeroConf: number;
        minimalBatched?: number;
    };
    fees: {
        minerFees: number;
        percentage: number;
        maximalRoutingFee?: number;
    };
};

type ReversePairTypeTaproot = PairType & {
    limits: PairLimits;
    fees: {
        percentage: number;
        minerFees: ReverseMinerFees;
    };
};

type ChainPairTypeTaproot = PairType & {
    limits: PairLimits & {
        maximalZeroConf: number;
    };
    fees: {
        percentage: number;
        minerFees: {
            server: number;
            user: {
                claim: number;
                lockup: number;
            };
        };
    };
};

type SubmarinePairsTaproot = Record<
    string,
    Record<string, SubmarinePairTypeTaproot>
>;

type ReversePairsTaproot = Record<
    string,
    Record<string, ReversePairTypeTaproot>
>;

type ChainPairsTaproot = Record<string, Record<string, ChainPairTypeTaproot>>;

type Pairs = {
    [SwapType.Submarine]: SubmarinePairsTaproot;
    [SwapType.Reverse]: ReversePairsTaproot;
    [SwapType.Chain]: ChainPairsTaproot;
};

type PartialSignature = {
    pubNonce: Uint8Array;
    signature: Uint8Array;
};

type SwapTreeLeaf = {
    output: string;
    version: number;
};

type SwapTree = {
    claimLeaf: SwapTreeLeaf;
    refundLeaf: SwapTreeLeaf;
};

type CommitmentLockupDetails = {
    contract: string;
    claimAddress: string;
    timelock: number;
};

type SubmarineCreatedResponse = {
    id: string;
    address: string;
    bip21: string;
    swapTree: SwapTree;
    acceptZeroConf: boolean;
    expectedAmount: number;
    claimPublicKey: string;
    timeoutBlockHeight: number;
    blindingKey?: string;
    claimAddress?: string;
};

type ReverseCreatedResponse = {
    id: string;
    invoice: string;
    swapTree: SwapTree;
    lockupAddress: string;
    timeoutBlockHeight: number;
    onchainAmount: number;
    refundPublicKey?: string;
    blindingKey?: string;
    refundAddress?: string;
};

type ChainSwapDetails = {
    swapTree: SwapTree;
    lockupAddress: string;
    serverPublicKey: string;
    timeoutBlockHeight: number;
    amount: number;
    blindingKey?: string;
    refundAddress?: string;
    claimAddress?: string;
    bip21?: string;
};

type ChainSwapCreatedResponse = {
    id: string;
    claimDetails: ChainSwapDetails;
    lockupDetails: ChainSwapDetails;
};

type ChainSwapTransaction = {
    transaction: {
        id: string;
        hex?: string;
    };
    timeout: {
        blockHeight: number;
        eta?: number;
    };
};

type RestorableSwapDetails = {
    tree: SwapTree;
    keyIndex: number;
    lockupAddress: string;
    serverPublicKey: string;
    timeoutBlockHeight: number;
    blindingKey?: string;
    amount?: number;
    transaction?: { id: string; vout: number };
    preimageHash?: string;
};

export type RestorableSwap = {
    id: string;
    type: SwapType;
    status: string;
    from: string;
    to: string;
    createdAt: number;
    claimPrivateKey?: string;
    claimDetails?: RestorableSwapDetails;
    refundDetails?: RestorableSwapDetails;
};

export type LockupTransaction = {
    id: string;
    hex: string;
    timeoutBlockHeight: number;
    timeoutEta?: number;
};

export type SwapStatus = {
    status: string;
    failureReason?: string;
    zeroConfRejected?: boolean;
    transaction?: {
        id: string;
        hex: string;
    };
};

export type QuoteData = {
    quote: string;
    data: unknown;
};

export type QuoteCalldata = {
    to: Address;
    value: string;
    data: Hex;
};

export enum DexQuoteDirection {
    In = "in",
    Out = "out",
}

const sortDexQuotes = (
    quotes: QuoteData[],
    direction: DexQuoteDirection,
): QuoteData[] =>
    [...quotes].sort((first, second) => {
        const firstAmount = BigInt(first.quote);
        const secondAmount = BigInt(second.quote);

        if (firstAmount === secondAmount) {
            return 0;
        }

        if (direction === DexQuoteDirection.In) {
            return firstAmount > secondAmount ? -1 : 1;
        }

        return firstAmount < secondAmount ? -1 : 1;
    });

export const getPairs = async (options?: RequestInit): Promise<Pairs> => {
    const [submarine, reverse, chain] = await Promise.all([
        fetcher<SubmarinePairsTaproot>(
            "/v2/swap/submarine",
            undefined,
            options,
        ),
        fetcher<ReversePairsTaproot>("/v2/swap/reverse", undefined, options),
        fetcher<ChainPairsTaproot>("/v2/swap/chain", undefined, options),
    ]);

    return {
        [SwapType.Chain]: chain,
        [SwapType.Reverse]: reverse,
        [SwapType.Submarine]: submarine,
    };
};

// Returns the BOLT12 invoice for an offer without validating that it matches.
// Callers must verify with their own validator (host's
// `validateInvoiceForOffer`) since BOLT12 validation depends on Lightning
// crypto primitives not currently in this package.
export const fetchBolt12Invoice = (
    offer: string,
    amountSat: number,
): Promise<{ invoice: string }> =>
    fetcher<{ invoice: string }>("/v2/lightning/BTC/bolt12/fetch", {
        offer,
        amount: amountSat,
    });

export const fetchBip21Invoice = async (invoice: string) => {
    const log = getLogger();
    try {
        log.debug("Fetching BIP21 for invoice", invoice);
        const res = await fetcher<{ bip21: string; signature: string }>(
            `/v2/swap/reverse/${invoice}/bip21`,
        );
        return res;
    } catch {
        log.debug("No BIP21 found for invoice");
        return null;
    }
};

export const createSubmarineSwap = (
    from: string,
    to: string,
    invoice: string,
    pairHash: string,
    refundPublicKey?: string,
): Promise<SubmarineCreatedResponse> =>
    fetcher("/v2/swap/submarine", {
        from,
        to,
        invoice,
        refundPublicKey,
        pairHash,
        referralId: getReferralId(),
    });

export const createReverseSwap = (
    from: string,
    to: string,
    invoiceAmount: number,
    preimageHash: string,
    pairHash: string,
    claimPublicKey?: string,
    claimAddress?: string,
): Promise<ReverseCreatedResponse> =>
    fetcher("/v2/swap/reverse", {
        from,
        to,
        invoiceAmount,
        preimageHash,
        claimPublicKey,
        claimAddress,
        referralId: getReferralId(),
        pairHash,
    });

export const createChainSwap = (
    from: string,
    to: string,
    userLockAmount: number | undefined,
    preimageHash: string,
    claimPublicKey: string | undefined,
    refundPublicKey: string | undefined,
    claimAddress: string | undefined,
    pairHash: string,
): Promise<ChainSwapCreatedResponse> =>
    fetcher("/v2/swap/chain", {
        from,
        to,
        preimageHash,
        claimPublicKey,
        refundPublicKey,
        claimAddress,
        pairHash,
        referralId: getReferralId(),
        userLockAmount,
    });

export const getPartialRefundSignature = async (
    id: string,
    type: SwapType,
    pubNonce: Uint8Array,
    transactionHex: string,
    index: number,
): Promise<PartialSignature> => {
    checkCooperative();
    const res = await fetcher<{ pubNonce: string; partialSignature: string }>(
        `/v2/swap/${
            type === SwapType.Submarine ? "submarine" : "chain"
        }/${id}/refund`,
        {
            index,
            pubNonce: hex.encode(pubNonce),
            transaction: transactionHex,
        },
    );
    return {
        pubNonce: hex.decode(res.pubNonce),
        signature: hex.decode(res.partialSignature),
    };
};

export const getPartialReverseClaimSignature = async (
    id: string,
    preimage: Uint8Array,
    pubNonce: Uint8Array,
    transactionHex: string,
    index: number,
): Promise<PartialSignature> => {
    checkCooperative();
    const res = await fetcher<{ pubNonce: string; partialSignature: string }>(
        `/v2/swap/reverse/${id}/claim`,
        {
            index,
            preimage: hex.encode(preimage),
            pubNonce: hex.encode(pubNonce),
            transaction: transactionHex,
        },
    );
    return {
        pubNonce: hex.decode(res.pubNonce),
        signature: hex.decode(res.partialSignature),
    };
};

export const getSubmarineClaimDetails = async (id: string) => {
    const res = await fetcher<{
        pubNonce: string;
        preimage: string;
        transactionHash: string;
    }>(`/v2/swap/submarine/${id}/claim`);
    return {
        pubNonce: hex.decode(res.pubNonce),
        preimage: hex.decode(res.preimage),
        transactionHash: hex.decode(res.transactionHash),
    };
};

export const postSubmarineClaimDetails = (
    id: string,
    pubNonce: Uint8Array,
    partialSignature: Uint8Array,
) => {
    checkCooperative();
    return fetcher(`/v2/swap/submarine/${id}/claim`, {
        pubNonce: hex.encode(pubNonce),
        partialSignature: hex.encode(partialSignature),
    });
};

export const getEipRefundSignature = (id: string, type: SwapType) => {
    checkCooperative();
    return fetcher<{ signature: Hex }>(`/v2/swap/${type}/${id}/refund`);
};

export const getFeeEstimations = () =>
    fetcher<Record<string, number>>("/v2/chain/fees");

export const getNodeStats = () =>
    fetcher<{
        BTC: {
            total: {
                capacity: number;
                channels: number;
                peers: number;
                oldestChannel: number;
            };
        };
    }>("/v2/nodes/stats");

export const getContracts = () =>
    fetcher<Record<string, Contracts>>("/v2/chain/contracts");

export const getCommitmentLockupDetails = (currency: string) =>
    fetcher<CommitmentLockupDetails>(`/v2/commitment/${currency}/details`);

export const postCommitmentSignature = (
    currency: string,
    swapId: string,
    signature: Hex,
    transactionHash: string,
    logIndex?: number,
    maxOverpaymentPercentage?: number,
) =>
    fetcher<object>(`/v2/commitment/${currency}`, {
        swapId,
        signature,
        transactionHash,
        logIndex,
        maxOverpaymentPercentage,
    });

export const postCommitmentRefundSignature = (
    currency: string,
    transactionHash: string,
    refundAddressSignature: Hex,
    logIndex?: number,
) =>
    fetcher<{ signature: Hex }>(`/v2/commitment/${currency}/refund`, {
        transactionHash,
        refundAddressSignature,
        logIndex,
    });

// API-only transaction broadcast. Host wraps this with `broadcastToExplorer`
// fallback in `src/utils/blockchain.ts` to race the two channels.
export const broadcastApiTransaction = (
    asset: string,
    txHex: string,
): Promise<{ id: string }> =>
    fetcher<{ id: string }>(`/v2/chain/${asset}/transaction`, {
        hex: txHex,
    });

export const getLockupTransaction = async (
    id: string,
    type: SwapType,
): Promise<LockupTransaction> => {
    switch (type) {
        case SwapType.Submarine:
            return fetcher<{
                id: string;
                hex: string;
                timeoutBlockHeight: number;
                timeoutEta?: number;
            }>(`/v2/swap/submarine/${id}/transaction`);

        case SwapType.Chain: {
            const res = await getChainSwapTransactions(id);
            return {
                id: res.userLock.transaction.id,
                hex: res.userLock.transaction.hex ?? "",
                timeoutEta: res.userLock.timeout.eta,
                timeoutBlockHeight: res.userLock.timeout.blockHeight,
            };
        }

        default:
            throw `cannot get lockup transaction for swap type ${type}`;
    }
};

export const getReverseTransaction = (id: string) =>
    fetcher<{
        id: string;
        hex: string;
        timeoutBlockHeight: number;
    }>(`/v2/swap/reverse/${id}/transaction`);

export const getSwapStatus = (id: string) =>
    fetcher<SwapStatus>(`/v2/swap/${id}`);

export const getChainSwapClaimDetails = (id: string) =>
    fetcher<{
        pubNonce: string;
        publicKey: string;
        transactionHash: string;
    }>(`/v2/swap/chain/${id}/claim`);

export const postChainSwapDetails = (
    id: string,
    preimage: string | undefined,
    signature: { pubNonce: string; partialSignature: string } | undefined,
    toSign?: { pubNonce: string; transaction: string; index: number },
) => {
    checkCooperative();
    return fetcher<{
        pubNonce: string;
        partialSignature: string;
    }>(`/v2/swap/chain/${id}/claim`, {
        preimage,
        signature,
        toSign,
    });
};

export const getChainSwapTransactions = (id: string) =>
    fetcher<{
        userLock: ChainSwapTransaction;
        serverLock: ChainSwapTransaction;
    }>(`/v2/swap/chain/${id}/transactions`);

export const getChainSwapNewQuote = (id: string) =>
    fetcher<{ amount: number }>(`/v2/swap/chain/${id}/quote`);

export const acceptChainSwapNewQuote = (id: string, amount: number) =>
    fetcher<object>(`/v2/swap/chain/${id}/quote`, { amount });

export const getSubmarinePreimage = (id: string) =>
    fetcher<{ preimage: string }>(`/v2/swap/submarine/${id}/preimage`);

export const getRestorableSwaps = (
    xpub: string,
    pagination?: { startIndex: number; limit: number },
    signal?: AbortSignal,
) => {
    const options = signal === undefined ? undefined : { signal };
    return fetcher<RestorableSwap[]>(
        `/v2/swap/restore`,
        { xpub, pagination },
        options,
        30_000,
    );
};

export const assetRescueSetup = (
    asset: string,
    swapId: string,
    transactionId: string,
    vout: number,
    destination: string,
) =>
    fetcher<{
        musig: {
            serverPublicKey: string;
            pubNonce: string;
            message: string;
        };
        transaction: string;
    }>(`/v2/asset/${asset}/rescue/setup`, {
        swapId,
        transactionId,
        vout,
        destination,
    });

export const assetRescueBroadcast = (
    asset: string,
    swapId: string,
    pubNonce: Uint8Array,
    partialSignature: Uint8Array,
) =>
    fetcher<{
        transactionId: string;
    }>(`/v2/asset/${asset}/rescue/broadcast`, {
        swapId,
        pubNonce: hex.encode(pubNonce),
        partialSignature: hex.encode(partialSignature),
    });

export const quoteDexAmountIn = async (
    chain: string,
    tokenIn: string,
    tokenOut: string,
    amountIn: bigint,
): Promise<QuoteData[]> => {
    if (amountIn === 0n) {
        return [];
    }

    const params = new URLSearchParams();
    params.set("tokenIn", tokenIn);
    params.set("tokenOut", tokenOut);
    params.set("amountIn", amountIn.toString());
    return sortDexQuotes(
        await fetcher(`/v2/quote/${chain}/in?${params.toString()}`),
        DexQuoteDirection.In,
    );
};

export const quoteDexAmountOut = async (
    chain: string,
    tokenIn: string,
    tokenOut: string,
    amountOut: bigint,
): Promise<QuoteData[]> => {
    if (amountOut === 0n) {
        return [];
    }

    const params = new URLSearchParams();
    params.set("tokenIn", tokenIn);
    params.set("tokenOut", tokenOut);
    params.set("amountOut", amountOut.toString());
    return sortDexQuotes(
        await fetcher(`/v2/quote/${chain}/out?${params.toString()}`),
        DexQuoteDirection.Out,
    );
};

export const encodeDexQuote = (
    chain: string,
    recipient: string,
    amountIn: bigint,
    amountOutMin: bigint,
    data: QuoteData["data"],
) =>
    fetcher<{ calls: QuoteCalldata[] }>(`/v2/quote/${chain}/encode`, {
        recipient,
        amountIn: amountIn.toString(),
        amountOutMin: amountOutMin.toString(),
        data,
    });

export type {
    Pairs,
    CommitmentLockupDetails,
    PartialSignature,
    ChainPairTypeTaproot,
    ReversePairTypeTaproot,
    SubmarineCreatedResponse,
    SubmarinePairTypeTaproot,
    ReverseCreatedResponse,
    ChainSwapDetails,
    ChainSwapCreatedResponse,
};
